import type { Org } from '@p2b/contract';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { ErrorNote } from '../../App.js';
import { api } from '../../api.js';
import { label, useI18n } from '../../i18n.js';
import { formatKennitala } from '../orgParts.js';

/** The organization's profile (owners and admins edit it) and its verified domains (Mynstra registers them). */
export function SettingsTab({
  org,
  domains,
  canEdit,
  superadmin,
}: {
  org: Org;
  domains: string[];
  canEdit: boolean;
  superadmin: boolean;
}) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: org.name,
    kennitala: org.kennitala ? formatKennitala(org.kennitala) : '',
    address: org.address ?? '',
  });
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () =>
      api.updateOrg(org.slug, {
        name: form.name,
        kennitala: form.kennitala.trim() || null,
        address: form.address.trim() || null,
      }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['org', org.slug] });
      void qc.invalidateQueries({ queryKey: ['orgs'] });
    },
  });
  // Superadmins see how each domain admits people; others see the names only.
  const details = useQuery({
    queryKey: ['domains', org.slug],
    queryFn: () => api.domains(org.slug),
    enabled: superadmin,
  });
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setSaved(false);
    setForm({ ...form, [k]: e.target.value });
  };

  return (
    <div className="settings">
      <section className="settings-row">
        <div>
          <h2 className="sub-title">{t('profile')}</h2>
          <p className="muted">{t('profileHint')}</p>
        </div>
        <form
          className="panel form-panel"
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
        >
          <fieldset className="form-grid two" disabled={!canEdit}>
            <label className="span-all">
              {t('name')}
              <input required value={form.name} onChange={set('name')} />
            </label>
            <label>
              {t('kennitala')}
              <input
                inputMode="numeric"
                pattern="\d{6}-?\d{4}"
                placeholder="000000-0000"
                value={form.kennitala}
                onChange={set('kennitala')}
              />
            </label>
            <label>
              {t('slugLabel')}
              <input value={org.slug} readOnly disabled />
            </label>
            <label className="span-all">
              {t('address')}
              <input value={form.address} onChange={set('address')} />
            </label>
          </fieldset>
          <ErrorNote error={save.error} />
          {canEdit && (
            <div className="row">
              <button className="primary" disabled={save.isPending}>
                {t('save')}
              </button>
              {saved && <span className="sent">{t('saved')}</span>}
            </div>
          )}
        </form>
      </section>

      <section className="settings-row">
        <div>
          <h2 className="sub-title">{t('verifiedDomains')}</h2>
          <p className="muted">{t('domainsHint')}</p>
        </div>
        <div className="panel">
          {domains.length === 0 ? (
            <p className="muted">{t('noDomains')}</p>
          ) : (
            <ul className="domain-list">
              {domains.map((d) => {
                const info = details.data?.find((x) => x.domain === d);
                return (
                  <li key={d}>
                    <div>
                      <div className="cell-strong">{d}</div>
                      {info && (
                        <div className="sub">
                          {t('newJoinAs')} {label(t, 'role', info.defaultOrgRole)}
                          {info.entraTenantId ? ` · ${t('tenantLinked')}` : ''}
                        </div>
                      )}
                    </div>
                    <span className="tag dot ok">{t('verified')}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <p className="muted small">{t('domainsByMynstra')}</p>
        </div>
      </section>
    </div>
  );
}
